import { queryOptions } from '@tanstack/react-query';

import { fetchJson, isAbortedRequest, isMalformedResponse } from '../_shared/fetch-json';
import {
  MAPPINGS_QUERY_KEY,
  MappingsResponseSchema,
  MappingsSaveResponseSchema,
  SFDC_USERS_QUERY_KEY,
  SfdcUsersResponseSchema,
} from './rep-mapping-wire';
import type { MapDiff } from './store';

type ApiFetch = (input: string, init?: RequestInit & { timeoutMs?: number }) => Promise<Response>;

const MAPPINGS_PATH = '/api/tools/lead-scanner/mappings';

export const mappingsQueryOptions = (apiFetch: ApiFetch) =>
  queryOptions({
    queryKey: MAPPINGS_QUERY_KEY,
    queryFn: async () => (await fetchJson(apiFetch, MappingsResponseSchema, MAPPINGS_PATH)).mappings,
  });

export const sfdcUsersQueryOptions = (apiFetch: ApiFetch) =>
  queryOptions({
    queryKey: SFDC_USERS_QUERY_KEY,
    queryFn: async () => (await fetchJson(apiFetch, SfdcUsersResponseSchema, '/api/tools/lead-scanner/sfdc-users')).users,
  });

export type SaveOutcome = 'saved' | 'unconfirmed';

/**
 * Maps a failed save to its outcome. A body the schema rejects (a proxy/edge
 * layer answering 200 with its own body) and a cancelled request both leave
 * the server's state unknown, so neither may be treated as saved: the caller
 * keeps the draft and refetches. The route is idempotent (createMany
 * skipDuplicates + deleteMany), so a retry is safe. A genuine HTTP failure or
 * a network `TypeError` still rethrows.
 */
export function saveOutcomeFromFailure(err: unknown): SaveOutcome {
  if (isMalformedResponse(err) || isAbortedRequest(err)) return 'unconfirmed';
  throw err;
}

/** PUTs the staged diff. Resolves `'saved'` only for the route's own success body. */
export async function putMappings(apiFetch: ApiFetch, diff: MapDiff): Promise<SaveOutcome> {
  try {
    await fetchJson(apiFetch, MappingsSaveResponseSchema, MAPPINGS_PATH, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ add: diff.added, remove: diff.removed }),
      // Longer than apiFetch's 15 s default: the PUT is one transaction over the whole staged diff.
      timeoutMs: 60_000,
    });
  } catch (err) {
    return saveOutcomeFromFailure(err);
  }
  return 'saved';
}
